import type { TOC } from '@ember/component/template-only';
import { fn, get } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import {
  PrismBreadcrumb,
  PrismIcon,
  PrismLoadingBar,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq } from 'ember-truth-helpers';
import SettingsMappingModalsKpiSectionModal from 'lumen/components/settings/mapping/modals/kpi-section-modal';
import SettingsMappingModalsMappingTypeModal from 'lumen/components/settings/mapping/modals/mapping-type-modal';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import { BENI_PMS_MAPPING_TABS_TRANSLATION_KEY_MAPPING } from 'lumen/utils/constants/pms-mapping-constants';

interface SettingsMappingSignature {
  Args: {
    activeTab: unknown;
    getHierarchy: unknown;
    getMapping: unknown;
    isEndOfMapping: unknown;
    isLoading: unknown;
    isRouteLoading: unknown;
    isShowingKpiSectionModal: unknown;
    onChangeTab: unknown;
    tabs: unknown;
  };
  Blocks: {
    mappingHierarchy: [];
    mappingProgress: [];
    mappingTable: [];
  };
}

const SettingsMapping: TOC<SettingsMappingSignature> = <template>
  {{#if @isLoading}}
    <PrismLoadingBar />
  {{/if}}

  <SettingsPageTemplate
    @isFullHeight={{true}}
    @title={{t "settings.central_mapping.market_segmentation"}}
  >
    <:breadcrumb>
      <PrismBreadcrumb as |B|>
        <B.item
          @label={{t "settings.pms_mapping.mapping"}}
          @route="hotel.settings.central-mapping.menu"
        />
        <B.item
          @label={{t "general.benchmark_insight"}}
          @route="hotel.settings.central-mapping.menu"
        />
        <B.item
          @isCurrent={{true}}
          @label={{t "settings.central_mapping.market_segmentation"}}
        />
      </PrismBreadcrumb>
    </:breadcrumb>

    <:default>
      {{! @glint-nocheck }}
      <section class="card-tile h-full">
        <div class="ic-product-tour-mapping-tabs">
          <PrismTabsContainer
            @activeTabId={{@activeTab}}
            @onSelectTab={{@onChangeTab}}
            @visibleTabIds={{@tabs}}
            as |TabsContainer|
          >
            <TabsContainer.header>
              <:default as |Header|>
                {{#each @tabs as |tab|}}
                  <Header.tab @id={{tab}}>
                    {{t
                      (get BENI_PMS_MAPPING_TABS_TRANSLATION_KEY_MAPPING tab)
                    }}
                  </Header.tab>
                {{/each}}
              </:default>

              <:after>
                {{#unless @isRouteLoading}}
                  {{yield to="mappingProgress"}}
                {{/unless}}
              </:after>
            </TabsContainer.header>
          </PrismTabsContainer>
        </div>
        {{#unless @isRouteLoading}}
          {{#if
            (and (eq @activeTab "segment") this.access.hasSpiderRateCodeSync)
          }}
            <div
              class="flex h-full flex-col items-center justify-center gap-500"
              data-test-id="rate-code-sync-wrapper"
            >
              <h3 data-test-id="rate-code-sync-title">{{t
                  "revenue_insight.rate_code_sync.title"
                }}</h3>
              <LinkTo
                @model="rate-plans"
                @route="hotel.settings.spider.spider-dashboard"
                class="prism-button prism-button--primary"
                data-test-id="rate-code-sync-cta"
              >
                {{t "revenue_insight.rate_code_sync.cta"}}
                <PrismIcon
                  @color="default-inverse"
                  @name="arrow-right"
                  @size="small"
                  class="ml-100"
                />
              </LinkTo>
            </div>
          {{else}}
            <div>
              <section class="card-tile__content breakdown-tab-container">
                <div class="flex w-1/3 flex-row">
                  {{yield to="mappingHierarchy"}}
                </div>
                <div class="ml-400 w-2/3">
                  {{yield to="mappingTable"}}
                </div>
              </section>
            </div>
          {{/if}}
        {{/unless}}
      </section>
    </:default>
  </SettingsPageTemplate>

  {{#if @isShowingKpiSectionModal}}
    <SettingsMappingModalsKpiSectionModal
      @isEndOfMapping={{@isEndOfMapping}}
      @onCloseModal={{fn (mut @isShowingKpiSectionModal) false}}
    />
  {{else if @isShowingMappingTypeModal}}
    <SettingsMappingModalsMappingTypeModal
      @getHierarchy={{@getHierarchy}}
      @getMapping={{@getMapping}}
      @onCloseModal={{fn (mut @isShowingMappingTypeModal) false}}
    />
  {{/if}}
</template>;

export default SettingsMapping;
